import * as React from 'react'
import styled from 'styled-components'
import { IWindow } from 'static/interfaces'
import { agentService } from 'core/services'
import { Button, CheckBox, Spinner, InputText, InputTextArea } from 'components/atoms'
import Typography from 'components/atoms/_layouts/Typography'
import { COLORS } from 'static/constants'

declare var window: IWindow

interface IProps {
  id: number
  title: string
  connected: boolean
  onSubmit?: (mail: { subject: string; body: string; sendMail: boolean }) => Promise<void>
  connectionMailTemplateId: number | null
}

interface IMessagePreview {
  subject: string
  body: string
  missing_fields: string[]
}

export const ConnectionMessageModalBody = ({
  id,
  title,
  connected,
  onSubmit,
  connectionMailTemplateId,
}: IProps) => {
  const [loading, setLoading] = React.useState<boolean>(false)
  const [messagePreview, setMessagePreview] = React.useState<IMessagePreview>({
    subject: '',
    body: '',
    missing_fields: [],
  })
  const editable = typeof onSubmit === 'function'
  const [subject, setSubject] = React.useState<string>('')
  const [body, setBody] = React.useState<string>('')
  const [sendMail, setSendMail] = React.useState<boolean>(true)

  React.useEffect(() => {
    const fetchMessage = async () => {
      try {
        setLoading(true)
        const { messagePreview: newMessagePreview } = await agentService.getConnectMessagePreview(
          id,
          connectionMailTemplateId
        )
        setMessagePreview(newMessagePreview)
        setSubject(newMessagePreview.subject)
        setBody(newMessagePreview.body)
      } catch (error) {
        console.error('Failed to fetch message preview:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchMessage()
  }, [id, connectionMailTemplateId, editable])

  const submit = async () => {
    if (!onSubmit) {
      return
    }
    await onSubmit({ subject, body, sendMail })
    window.globalModal.closeModal()
  }

  return (
    <ConnectMessageModalBodyInner>
      <Typography type="subtitle" size="xl">
        {title}
      </Typography>

      {editable && (
        <CheckBox
          name="send_mail"
          label="メールを送信する"
          showLabelInline={true}
          checked={sendMail}
          onChangeHandler={e => setSendMail(e.target.checked)}
        />
      )}

      {sendMail && messagePreview.missing_fields.length > 0 && (
        <div className="warning">
          <Typography type="body" size="sm">
            {`不足情報があります: ${messagePreview.missing_fields.join(', ')}`}
          </Typography>
        </div>
      )}

      <div className="subtitle_wrapper">
        <Typography type="subtitle" size="xl">
          タイトル
        </Typography>
        {editable && sendMail && (
          <Typography type="body" size="lg" color="subtext">
            編集可能
          </Typography>
        )}
      </div>

      {loading ? (
        <Spinner />
      ) : (
        <InputText
          name="subject"
          value={subject}
          disabled={!sendMail}
          onChangeHandler={e => setSubject(e.target.value)}
        />
      )}

      <div className="subtitle_wrapper">
        <Typography type="subtitle" size="xl">
          本文
        </Typography>
        {editable && sendMail && (
          <Typography type="body" size="lg" color="subtext">
            編集可能
          </Typography>
        )}
      </div>

      <div className="message_form">
        {loading ? (
          <Spinner />
        ) : (
          <InputTextArea
            name="body"
            value={body}
            disabled={!sendMail}
            onChangeHandler={e => setBody(e.target.value)}
          />
        )}
      </div>

      {!connected && (
        <Button primary={true} handleClick={submit}>
          つなぐ
        </Button>
      )}
    </ConnectMessageModalBodyInner>
  )
}

const ConnectMessageModalBodyInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .message_form {
    border: 1px solid ${COLORS.divider};
    border-radius: 4px;
    margin-bottom: 16px;
    max-height: 400px;
    overflow: auto;
  }

  .subtitle_wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .warning {
    background-color: ${COLORS.useallyBG};
    border: 1px solid ${COLORS.useally};
    border-radius: 4px;
    padding: 8px 12px;
    margin-bottom: 8px;
  }
`
